import { createRouter, createWebHistory } from "vue-router";
import { useMainStore } from "@/pinia/main";
import { useAuthStore } from "@/pinia/auth";
import config from "@/config";
import routeGuards from "./routeGuards";
import { lazyImportRoute } from "@/lazy";

const Public = lazyImportRoute("Public");
const Respond = lazyImportRoute("public-elements/Respond");
const IdeaPage = lazyImportRoute("public-elements/IdeaPage");
const PostPage = lazyImportRoute("public-elements/PostPage");
const FrontPage = lazyImportRoute("public-elements/FrontPage");
const TokenHandler = lazyImportRoute("public-elements/TokenHandler");
const GroupFrontPage = lazyImportRoute("public-elements/GroupFrontPage");

const Auth = lazyImportRoute("Auth");
const Login = lazyImportRoute("auth-elements/Login");
const OtpLogin = lazyImportRoute("auth-elements/OtpLogin");
const ResetAsk = lazyImportRoute("auth-elements/ResetAsk");
const Register = lazyImportRoute("auth-elements/Register");
const SetupConfirm = lazyImportRoute("auth-elements/SetupConfirm");
const SuomiFiRedirect = lazyImportRoute("auth-elements/SuomiFiRedirect");
const SuomiFiLogin = lazyImportRoute("auth-elements/SuomiFiLogin");
const OpenIdRedirect = lazyImportRoute("auth-elements/OpenIdRedirect");
const SuomiFiLogout = lazyImportRoute("auth-elements/SuomiFiLogout");
const EmailConfirm = lazyImportRoute("auth-elements/EmailConfirm");

const Admin = lazyImportRoute("Admin");
const AdminGroups = lazyImportRoute("admin-elements/Groups");
const AdminCustomers = lazyImportRoute("admin-elements/Customers");

const ResponsePanelTracker = lazyImportRoute("ResponsePanelTracker");

const Browser = lazyImportRoute("Browser");
const PostBrowser = lazyImportRoute("browser-elements/PostBrowser");
const ProjectBrowser = lazyImportRoute("browser-elements/ProjectBrowser");
const AssetBrowser = lazyImportRoute("browser-elements/AssetBrowser");
const IdeaBrowser = lazyImportRoute("browser-elements/IdeaBrowser");
const ProposalBrowser = lazyImportRoute("browser-elements/ProposalBrowser");
const QuestionnaireBrowser = lazyImportRoute("browser-elements/QuestionnaireBrowser");

const Groups = lazyImportRoute("Groups");
const GroupBrowser = lazyImportRoute("group-elements/GroupBrowser");

const User = lazyImportRoute("User");
const UserData = lazyImportRoute("user-elements/UserData");
const UserDisabled = lazyImportRoute("user-elements/UserDisabled");
const UserBilling = lazyImportRoute("user-elements/UserBilling");
const UserSettings = lazyImportRoute("user-elements/UserSettings");
const UserSubscription = lazyImportRoute("user-elements/UserSubscription");

const PostEditor = lazyImportRoute("PostEditor");
const PostEdit = lazyImportRoute("post-editor-elements/PostEdit");
const PostSettings = lazyImportRoute("post-editor-elements/PostSettings");
const PostPreview = lazyImportRoute("post-editor-elements/PostPreview");
const PostGroupSettings = lazyImportRoute("post-editor-elements/PostGroupSettings");
const PostPublish = lazyImportRoute("post-editor-elements/PostPublish");
const PostTranslate = lazyImportRoute("post-editor-elements/PostTranslate");

const AssetSettings = lazyImportRoute("asset-elements/AssetSettings");

const QuestionnaireEditor = lazyImportRoute("QuestionnaireEditor");
const QuestionnaireEdit = lazyImportRoute("questionnaire-editor-elements/QuestionnaireEdit");
const QuestionnaireSettings = lazyImportRoute(
  "questionnaire-editor-elements/QuestionnaireSettings"
);
const QuestionnairePreview = lazyImportRoute("questionnaire-editor-elements/QuestionnairePreview");
const QuestionnairePublish = lazyImportRoute("questionnaire-editor-elements/QuestionnairePublish");
const QuestionnaireResults = lazyImportRoute("questionnaire-editor-elements/QuestionnaireResults");
const QuestionnaireAnalyse = lazyImportRoute("questionnaire-editor-elements/QuestionnaireAnalyse");
const QuestionnaireTranslate = lazyImportRoute(
  "questionnaire-editor-elements/translate/QuestionnaireTranslate"
);

const ProjectEditor = lazyImportRoute("ProjectEditor");
const ProjectSettings = lazyImportRoute("project-editor-elements/ProjectSettings");
const ProjectRespondents = lazyImportRoute("project-editor-elements/ProjectRespondents");
const ProposalPage = lazyImportRoute("public-elements/ProposalPage");

// const Setup = lazyImportRoute("Setup");
// const SetupGroups = lazyImportRoute("setup-elements/SetupGroups");
// const SetupProfile = lazyImportRoute("setup-elements/SetupProfile");

const StaticRoute = lazyImportRoute("public-elements/StaticRoute");

// const ErrorPage = lazyImportRoute("ErrorPage");
const PageNotFound = lazyImportRoute("PageNotFound");

// Vue.use(VueRouter);

// //////////////////////// Router ///////////////////////////////////////////////////////
//
// meta: {
//     isPublic: indicates a public router branch. This is used in app for deciding if to
//         apply custom tenant styles, show internal navigation options, etc.
//     isPreview: indicates a preview router branch, i.e., routes where even though you
//         are logged in, components should behave like it is a public route
//     requiresAuth: indicates a route branch where the user must be logged in. Used in
//         app to prevent navigation and redirect to login
//     requireUnauth: indicates a route branch where the user must be logged out, used in
//         app to log current user out before proceeding with navigation.
//     requiresGroup: indicates a route branch that requires that the user has at least
//         one group. Used in app to prevent navigation if the config option
//         config.accessConfig.allowGroupCreation is `true`
// }

const routeDefinition = {
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      component: Public,
      meta: {
        isPublic: true,
      },
      children: [
        {
          path: "",
          name: "FrontPage",
          component: FrontPage,
          beforeEnter: (to, from, next) => {
            if (config.vantaaTranslations) {
              // only vantaa is allowed to see the frontpage
              next();
            } else {
              if (useAuthStore().isAuthenticated) {
                // user is logged in...
                if (useMainStore().hasGroups) {
                  // if user has groups, go to q browser
                  next({ name: "QuestionnaireBrowser", query: to.query });
                } else {
                  // otherwise go to user responses because user is likely someone who
                  // made account to answer questionnaire
                  next({ name: "Data", query: to.query });
                }
              } else {
                next({ name: "Login", query: to.query });
              }
            }
          },
        },
        {
          path: "/p/:postId",
          name: "PostPage",
          component: PostPage,
        },
        {
          path: "/i/:projectId/:ideaId",
          name: "IdeaPage",
          component: IdeaPage,
        },
        {
          path: "/o/:projectId/:proposalId",
          name: "ProposalPage",
          component: ProposalPage,
        },
        {
          path: "/q/:questionnaireId",
          name: "Respond",
          component: Respond,
        },
        {
          path: "/t/:token",
          name: "TokenHandler",
          component: TokenHandler,
        },
        {
          path: "/g/:groupId",
          name: "GroupFrontPage",
          component: GroupFrontPage,
        },
      ],
    },
    {
      path: "/admin",
      component: Admin,
      beforeEnter: isSuper("any"),
      meta: {
        isSuper: true,
        requiresAuth: true,
        analyticsIgnore: true,
        meta: { titleKey: "meta.admin.main" },
      },
      children: [
        {
          path: "",
          name: "Admin",
          redirect: "groups",
        },
        {
          path: "groups",
          name: "AdminGroups",
          component: AdminGroups,
          beforeEnter: isSuper("licensing"),
        },
        {
          path: "customers",
          name: "AdminCustomers",
          component: AdminCustomers,
          beforeEnter: isSuper("licensing"),
        },
      ],
    },
    {
      path: "/auth",
      component: Auth,
      meta: {
        isPublic: true,
      },
      children: [
        {
          path: "login",
          name: "Login",
          component: Login,
          meta: {
            requiresUnauth: true,
            titleKey: "meta.auth.login",
          },
        },
        {
          path: "otp",
          name: "OtpLogin",
          component: OtpLogin,
          meta: {
            titleKey: "meta.auth.otp",
          },
        },
        {
          path: "register",
          name: "Register",
          component: Register,
          meta: {
            requiresUnauth: true,
            titleKey: "meta.auth.register",
          },
        },
        {
          path: "email/confirm/:tokenId",
          name: "EmailConfirm",
          component: EmailConfirm,
          meta: {
            analyticsIgnore: true,
            titleKey: "meta.auth.confirmEmail",
          },
        },
        {
          path: "email/change/confirm/:tokenId",
          name: "EmailChangeConfirm",
          component: EmailConfirm,
          meta: {
            analyticsIgnore: true,
            titleKey: "meta.auth.confirmEmailChange",
          },
        },
        {
          path: "reset",
          name: "ResetAsk",
          component: ResetAsk,
          meta: {
            requiresUnauth: true,
            titleKey: "meta.auth.reset",
          },
        },
        {
          path: "reset/:tokenId",
          name: "ResetConfirm",
          component: SetupConfirm,
          meta: {
            requiresUnauth: true,
            analyticsIgnore: true,
            titleKey: "meta.auth.reset",
          },
        },
        {
          path: "setup/:tokenId",
          name: "SetupConfirm",
          component: SetupConfirm,
          meta: {
            requiresUnauth: true,
            analyticsIgnore: true,
            titleKey: "meta.auth.setupPassword",
          },
        },
        {
          path: "suomifi-redirect",
          name: "SuomiFiRedirect",
          component: SuomiFiRedirect,
          meta: {
            analyticsIgnore: true,
            titleKey: "meta.auth.login",
          },
        },
        {
          path: "suomifi-login/:questionnaireId/:groupId",
          name: "SuomiFiLogin",
          component: SuomiFiLogin,
          meta: {
            analyticsIgnore: true,
            titleKey: "meta.auth.login",
          },
        },
        {
          path: "openid-redirect",
          name: "OpenIdRedirect",
          component: OpenIdRedirect,
          meta: {
            analyticsIgnore: true,
          },
        },
        {
          path: "suomifi-logout",
          name: "SuomiFiLogout",
          component: SuomiFiLogout,
          meta: {
            analyticsIgnore: true,
          },
        },
      ],
    },
    {
      path: "/response/paneltracker",
      component: ResponsePanelTracker,
      meta: {
        requiresAuth: true,
        requiresGroup: true,
      },
    },
    {
      path: "/browse",
      component: Browser,
      meta: {
        requiresAuth: true,
        requiresGroup: true,
      },
      children: [
        {
          path: "",
          redirect: "/browse/questionnaires",
        },
        {
          path: "questionnaires",
          name: "QuestionnaireBrowser",
          component: QuestionnaireBrowser,
          meta: { titleKey: "meta.browser.questionnaires" },
        },
        {
          path: "posts",
          name: "PostBrowser",
          component: PostBrowser,
          meta: { titleKey: "meta.browser.posts" },
        },
        {
          path: "projects",
          name: "ProjectBrowser",
          component: ProjectBrowser,
          meta: { titleKey: "meta.browser.projects" },
        },
        {
          path: "assets",
          name: "AssetBrowser",
          component: AssetBrowser,
          meta: { titleKey: "meta.browser.assets" },
        },
        {
          path: "bulk-edit",
          name: "PostBulkEdit",
          component: PostGroupSettings,
        },
        {
          path: "/asset/edit/:assetId",
          name: "AssetSettings",
          component: AssetSettings,
          meta: { titleKey: "meta.browser.assets" },
        },
      ],
    },
    {
      path: "/groups",
      component: Groups,
      meta: {
        requiresAuth: true,
        requiresGroup: true,
      },
      children: [
        {
          path: "",
          name: "Groups",
          redirect: "browse",
        },
        {
          path: "browse",
          name: "GroupBrowser",
          component: GroupBrowser,
        },
      ],
    },
    {
      path: "/user",
      component: User,
      meta: {
        isPublic: true,
        requiresAuth: true,
        titleKey: "meta.user.main",
      },
      children: [
        {
          path: "",
          redirect: "profile",
        },
        {
          path: "settings",
          name: "Settings",
          component: UserSettings,
          meta: {
            titleKey: "meta.user.settings",
          },
        },
        {
          path: "data",
          name: "Data",
          component: UserData,
          meta: {
            titleKey: "meta.user.data",
          },
        },
        {
          path: "subscription",
          name: "Subscription",
          component: UserSubscription,
          meta: {
            titleKey: "meta.user.subscription",
          },
        },
        {
          path: "billing",
          name: "Billing",
          component: UserBilling,
          meta: {
            titleKey: "meta.user.billing",
          },
        },
        {
          path: "disabled",
          name: "UserDisabled",
          component: UserDisabled,
          meta: {
            titleKey: "meta.user.disabled",
          },
        },
      ],
    },
    {
      path: "/post",
      meta: {
        requiresAuth: true,
        requiresGroup: true,
        titleKey: "meta.browser.posts",
      },
      component: PostEditor,
      children: [
        {
          path: "",
          redirect: "/browse/posts",
        },
        {
          path: "edit/:postId",
          name: "PostEdit",
          component: PostEdit,
          meta: { forceGroupIdBy: "postId", titleKey: "meta.postEditor.edit" },
        },
        {
          path: "settings/:postId",
          name: "PostSettings",
          component: PostSettings,
          meta: { forceGroupIdBy: "postId", titleKey: "meta.postEditor.edit" },
        },
        {
          path: "translate/:postId",
          name: "PostTranslate",
          component: PostTranslate,
          meta: { forceGroupIdBy: "postId", titleKey: "meta.postEditor.translate" },
        },
        {
          path: "preview/:postId",
          name: "PostPreview",
          component: PostPreview,
          meta: { forceGroupIdBy: "postId", isPreview: true, titleKey: "meta.postEditor.preview" },
        },
        {
          path: "publish/:postId",
          name: "PostPublish",
          component: PostPublish,
          meta: { forceGroupIdBy: "postId", titleKey: "meta.postEditor.publish" },
        },
      ],
    },
    {
      path: "/questionnaire",
      meta: {
        isQuestionnaireEditor: true,
        requiresAuth: true,
        requiresGroup: true,
        titleKey: "meta.browser.questionnaires",
      },
      beforeEnter: routeGuards.resetEditorStateGuard,
      component: QuestionnaireEditor,
      children: [
        {
          path: "",
          redirect: "/browse/questionnaires",
        },
        {
          path: "edit/:questionnaireId",
          name: "QuestionnaireEdit",
          component: QuestionnaireEdit,
          meta: { forceGroupIdBy: "questionnaireId", titleKey: "meta.questionnaireEditor.edit" },
        },
        {
          path: "settings/:questionnaireId",
          name: "QuestionnaireSettings",
          component: QuestionnaireSettings,
          meta: { forceGroupIdBy: "questionnaireId", titleKey: "meta.questionnaireEditor.edit" },
        },
        {
          path: "translate/:questionnaireId",
          name: "QuestionnaireTranslate",
          component: QuestionnaireTranslate,
          meta: {
            forceGroupIdBy: "questionnaireId",
            titleKey: "meta.questionnaireEditor.translate",
          },
        },
        {
          path: "preview/:questionnaireId",
          name: "QuestionnairePreview",
          component: QuestionnairePreview,
          meta: {
            forceGroupIdBy: "questionnaireId",
            isPreview: true,
            titleKey: "meta.questionnaireEditor.preview",
          },
        },
        {
          path: "publish/:questionnaireId",
          name: "QuestionnairePublish",
          component: QuestionnairePublish,
          meta: { forceGroupIdBy: "questionnaireId", titleKey: "meta.questionnaireEditor.publish" },
        },
        {
          path: "analyse/:questionnaireId/:tab?",
          name: "QuestionnaireAnalyse",
          component: QuestionnaireAnalyse,
          meta: { forceGroupIdBy: "questionnaireId", titleKey: "meta.questionnaireEditor.analyse" },
        },
        {
          path: "results/:questionnaireId",
          name: "QuestionnaireResults",
          component: QuestionnaireResults,
          meta: { forceGroupIdBy: "questionnaireId", titleKey: "meta.questionnaireEditor.results" },
        },
      ],
    },
    {
      path: "/project",
      meta: {
        isProjectEditor: true,
        requiresAuth: true,
        requiresGroup: true,
        titleKey: "meta.browser.projects",
      },
      component: ProjectEditor,
      children: [
        {
          path: "",
          redirect: "/browse/projects",
        },
        {
          path: "settings/:projectId",
          name: "ProjectSettings",
          component: ProjectSettings,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.settings" },
        },
        {
          path: "ideas/:projectId",
          name: "IdeaBrowser",
          component: IdeaBrowser,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.ideas" },
        },
        {
          path: "ideas/:projectId/filter-by-respondent-id/:respondentId",
          name: "IdeaBrowserFilterByRespondentId",
          component: IdeaBrowser,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.ideas" },
        },
        {
          path: "ideas/:projectId/filter-by-proposal-id/:proposalId",
          name: "IdeaBrowserFilterByProposalId",
          component: IdeaBrowser,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.ideas" },
        },
        {
          path: "proposals/:projectId",
          name: "ProposalBrowser",
          component: ProposalBrowser,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.proposals" },
        },
        {
          path: "proposals/:projectId/filter-by-id/:proposalId",
          name: "ProposalBrowserFilterById",
          component: ProposalBrowser,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.proposals" },
        },
        {
          path: "respondents/:projectId/:questionnaireId",
          name: "ProjectRespondents",
          component: ProjectRespondents,
          meta: { forceGroupIdBy: "projectId", titleKey: "meta.projectEditor.respondents" },
        },
      ],
    },
    /* {
        path: "/setup",
        component: Setup,
        meta: { requiresAuth: true },
        children: [{
            path: "groups",
            name: "SetupGroups",
            component: SetupGroups,
        }, {
            path: "profile",
            name: "SetupProfile",
            component: SetupProfile,
        }],
    },*/ /* {
        path: "/error/:errorCode",
        name: "ErrorPage",
        component: ErrorPage,
    },*/ {
      path: "/:pathMatch(.*)*",
      name: "PageNotFound",
      component: PageNotFound,
      meta: {
        titleKey: "pageNotFound.metaTitle",
      },
    },
  ],
};

// This is currently used for adding the /osbu route for V
if (config?.additionalStaticRoutes?.length) {
  config.additionalStaticRoutes.forEach((r) => {
    // Check that definition is OK
    if (!r?.path?.startsWith?.("/") || !["post"].includes?.(r?.type)) return;
    // Unshift is needed b/c of the way vue router handles route precedence
    routeDefinition.routes.unshift({
      path: r.path,
      component: StaticRoute,
      props: {
        type: r.type,
        resourceId: r.resourceId,
      },
      meta: {
        isPublic: true,
      },
    });
  });
}

export const router = createRouter(routeDefinition);

// ////////////////////// Error handling /////////////////////////////////////////////////
//
// Chunk cache busting + lazy loading means that right after a deploy, users who have not
// reloaded since the deploy, will get errors since the chunks they are trying to load
// have disappeared.
//
// See:
// https://stackoverflow.com/questions/59385650/vuejs-browser-caching-and-loading-chunk-failed-issue
// https://blog.francium.tech/vue-lazy-routes-loading-chunk-failed-9ee407bbd58
//
// This has the potential for an infinite loop :-(. TODO: figure out how to SENTRY if this
// is looping
//
router.onError((error) => {
  const message = "The app has been updated, please reload the page to get the latest changes.";
  if (
    navigator.onLine &&
    (error.name === "ChunkLoadError" || /loading chunk \d* failed./i.test(error.message))
  ) {
    /* eslint no-alert: "off" */
    if (window.confirm(message)) {
      window.location.reload();
    }
  }
});

// ////////////////////// Navigation guards //////////////////////////////////////////////
//
// NOTE: The order in which these are registered matters!!
//
// Check that app has inited
export function applyRouteGuards() {
  router.beforeEach(routeGuards.initGuard);
  // Check that idb has inited
  router.beforeEach(routeGuards.idbGuard);
  // Check that user is not disabled
  router.beforeEach(routeGuards.disabledGuard);
  // Check if we need to force active group
  router.beforeEach(routeGuards.forceActiveGroupGuard);
  // Check and apply authentication guards
  router.beforeEach(routeGuards.authGuard);
  // Check and apply other requirements (currently only requiresGroup)
  router.beforeEach(routeGuards.groupGuard);
  // Check and apply user language preference
  router.beforeEach(routeGuards.languageGuard);
  // In line guards for checking license access
}

// In line guard for checking superuser status
function isSuper(type) {
  return (to, from, next) => {
    if (useMainStore().isSuper(type)) {
      next();
    } else {
      // if not allowed, just go to q browser
      next({ name: "QuestionnaireBrowser" });
    }
  };
}

export default router;
